import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Repeat, ChevronRight, ChevronDown, Loader2, Ban } from "lucide-react";
import { format } from "date-fns";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { OrderTableRow } from "@/components/orders/OrderTableRow";
import { InlineEditCell } from "@/components/orders/InlineEditCell";
import { InlineTrailerTypeCell } from "@/components/orders/InlineTrailerTypeCell";
import { InlineAssignCell } from "@/components/orders/InlineAssignCell";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ORDERS_PAGE_SIZE_OPTIONS } from "@/hooks/useOrdersQuery";
import { TablePagination } from "@/components/ui/table-pagination";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { CancelOrderDialog } from "@/components/orders/CancelOrderDialog";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { SortableHeader } from "@/components/ui/sortable-header";
import { useTableSort } from "@/hooks/useTableSort";
import { useOrderCancellation } from "@/hooks/useOrderDeletion";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Order, Customer, Tractor, Driver } from "@/types/tms";
import type { Location } from "@/types/tms";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { cn } from "@/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { OrderLoadStatusCell } from "@/components/orders/OrderLoadStatusCell";
import type { HistoricalLoadStatus } from "@/lib/order-load-status";
import { useOrderLoadStatusAtDelivery, type LoadStatusAtDeliveryMap } from "@/hooks/useOrderLoadStatusAtDelivery";
import { OrderDetailSheet } from "@/components/orders/OrderDetailSheet";
import { StarOrderButton } from "@/components/orders/StarOrderButton";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { OrderCard } from "@/components/orders/OrderCard";
import {
  isLateDeparture,
  isLateArrival,
} from "@/lib/order-time";
import { ReadyTimeDisplay, DeliveryTimeDisplay } from "@/components/orders/OrderTimeDisplay";
import { resolveOrderEndpoints } from "@/lib/order-endpoints";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  mapAndHydrateLegRows,
} from "@/lib/orders-with-legs";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { TrailerArrivedBadge } from "./TrailerArrivedBadge";
import { toLegsWithArrivedTrailer, type LegWithArrivedTrailer } from "@/lib/order-trailer-arrival";

type OrderWithRelations = Order & {
  billing_party: Customer;
  assigned_tractor: Tractor | null;
  assigned_operator: Driver | null;
};

interface OrderListViewProps {
  orders: OrderWithRelations[] | undefined;
  isLoading: boolean;
  /** True when a background refetch is in flight over already-rendered data. */
  isFetching?: boolean;
  /** Set when the orders query failed — rendered instead of the empty state. */
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onEdit: (order: Order) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  locations?: Location[];
  hasActiveFilter?: boolean;
}

function OrderRow({
  order,
  onEdit,
  onDelete,
  isDeleting,
  isChild = false,
  legNumber,
  totalLegs,
  parentUniqueId,
  onRowClick,
  isSelected,
  isChecked,
  onToggle,
  loadStatus,
  loadStatusHistorical,
  locations,
  childTrailerTypeLabel,
  legWithArrivedTrailer,
}: {
  order: OrderWithRelations;
  onEdit: (order: Order) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  isChild?: boolean;
  legNumber?: number;
  totalLegs?: number;
  parentUniqueId?: string;
  onRowClick?: (order: OrderWithRelations) => void;
  isSelected?: boolean;
  isChecked?: boolean;
  onToggle?: (id: string) => void;
  loadStatus?: string | null;
  loadStatusHistorical?: HistoricalLoadStatus | null;
  locations?: Location[];
  childTrailerTypeLabel?: string | null;
  legWithArrivedTrailer?: LegWithArrivedTrailer | null;
}) {
  return (
    <OrderTableRow
      order={order}
      onEdit={onEdit}
      isChild={isChild}
      legNumber={legNumber}
      totalLegs={totalLegs}
      parentUniqueId={parentUniqueId}
      onRowClick={onRowClick}
      isSelected={isSelected}
      isChecked={isChecked}
      onToggle={onToggle}
      loadStatus={loadStatus}
      loadStatusHistorical={loadStatusHistorical}
      locations={locations}
      childTrailerTypeLabel={childTrailerTypeLabel}
      legWithArrivedTrailer={legWithArrivedTrailer}
    />
  );
}

/** The order's legs (from order-leg-summaries) as LegWithArrivedTrailer values. */
function orderLegsWithArrivedTrailer(
  order: Pick<OrderWithRelations, "legs" | "status">,
  statuses?: Record<string, { location_status?: string | null }>,
): LegWithArrivedTrailer[] {
  return toLegsWithArrivedTrailer(order.legs ?? [], order.status, (id) => statuses?.[id]?.location_status);
}

/** Per-leg asset snapshot used to render comma-delimited multi-leg parent
 *  cells (trailer, tractor, operators) without expanding the row. */
type LegAssetSummary = {
  leg_sequence: number;
  trailer_id: string | null;
  trailer_type_id: string | null;
  tractor_name: string | null;
  operator_names: string[];
};


function ExpandableParentRow({
  order,
  onEdit,
  onDelete,
  isDeleting,
  onRowClick,
  selectedOrderId,
  isChecked,
  onToggle,
  loadStatuses,
  loadStatusAtDelivery,
  locations,
  legSummary,
  trailerTypesById,
}: {
  order: OrderWithRelations;
  onEdit: (order: Order) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  onRowClick?: (order: OrderWithRelations) => void;
  selectedOrderId?: string;
  isChecked?: boolean;
  onToggle?: (id: string) => void;
  loadStatuses?: Record<string, { load_status: string | null; trailer_type_id: string | null; location_status?: string | null }>;
  loadStatusAtDelivery?: LoadStatusAtDeliveryMap;
  locations?: Location[];
  legSummary?: LegAssetSummary[];
  trailerTypesById?: Map<string, string>;
}) {
  
  const [expanded, setExpanded] = useState(false);
  const lateDep = isLateDeparture(order);
  const lateArr = isLateArrival(order);
  const { isReadOnly } = useUserRole();
  const isTerminalStatus = order.status === "in_transit" || order.status === "delivered" || order.status === "canceled";
  const isTerminal = isTerminalStatus || isReadOnly;
  const warningTextClass = "text-[hsl(var(--aurora-status-warning))]";

  // Canonical short_code resolution shared by every order surface.
  // See src/lib/order-endpoints.ts.
  const { from: fromEndpoint, to: toEndpoint } = resolveOrderEndpoints(order, locations);
  const fromLabel = fromEndpoint.label;
  const toLabel = toEndpoint.label;

  // Fetch child legs only when expanded.
  // Phase 4: read from `orders_with_legs` (one row per leg, sourced from public.legs)
  // so this works for both legacy multi-leg orders (mirrored into legs) and new
  // multi-leg orders (which only have rows in legs, not in `orders`).
  const { data: childLegs, isLoading: childrenLoading } = useQuery({
    queryKey: ["order-children", order.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .eq("order_id", order.id)
        .order("leg_sequence", { ascending: true });
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
    enabled: expanded,
  });

  // Show the expand chevron when this parent has more than one leg in `legs`.
  // `leg_count` is attached by useOrdersQuery; fall back to the legacy heuristic
  // (`leg_sequence === 0` on a parent row) for any caller that hasn't been updated.
  const legCount = order.leg_count;
  const showExpandToggle =
    typeof legCount === "number" ? legCount > 1 : order.leg_sequence === 0;

  // For multi-leg parents, collapse per-leg trailer info into comma-delimited
  // display strings so the parent row reflects the leg-level trailer model
  // rather than the (legacy) parent-order mirrored trailer_id.
  const isMultiLegParent = showExpandToggle;
  // Once per row: the parent's line uses every leg, each expanded leg row its own.
  const legsWithArrivedTrailer = orderLegsWithArrivedTrailer(order, loadStatuses);
  const legWithArrivedTrailerBySequence = new Map(legsWithArrivedTrailer.map((l) => [l.leg_sequence, l]));
  const legTrailerIdText = isMultiLegParent && legSummary && legSummary.length
    ? legSummary.map((l) => l.trailer_id || "—").join(", ")
    : null;
  const legTrailerTypeText = isMultiLegParent && legSummary && legSummary.length
    ? legSummary
        .map((l) => (l.trailer_type_id && trailerTypesById?.get(l.trailer_type_id)) || "—")
        .join(", ")
    : null;
  // Tractor / operators follow the same per-leg model: `legs` is authoritative
  // and the parent-order columns are only a leg-1 mirror. Values are de-duped so
  // an asset shared across legs is shown once instead of repeated.
  const hasLegSummary = isMultiLegParent && !!legSummary && legSummary.length > 0;
  const dedupe = (values: string[]) => Array.from(new Set(values));
  const legTractorText = hasLegSummary
    ? dedupe(legSummary!.map((l) => l.tractor_name || "—")).join(", ")
    : null;
  const legOperatorText = hasLegSummary
    ? dedupe(
        legSummary!.map((l) =>
          l.operator_names.length ? dedupe(l.operator_names).join(" / ") : "—",
        ),
      ).join(", ")
    : null;



  return (
    <>
      <TableRow
        selected={selectedOrderId === order.id}
        className={cn(expanded ? "border-b-0" : "")}
        onClick={() => onRowClick?.(order)}
      >
        <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => onToggle?.(order.id)}
          />
        </TableCell>
        <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
          <StarOrderButton orderId={order.id} size="sm" />
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            {showExpandToggle && (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 p-0"
                onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </Button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium">{order.unique_id}</span>
                {order.is_recurring && (
                  <span title="Recurring order">
                    <Repeat className="h-3 w-3 text-muted-foreground" />
                  </span>
                )}
              </div>
              {showExpandToggle && (
                <div className="text-xs text-muted-foreground">
                  Multi-leg order
                </div>
              )}
              <TrailerArrivedBadge legs={legsWithArrivedTrailer} />
            </div>
          </div>
        </TableCell>
        <TableCell>
          {order.status === "canceled" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <OrderStatusBadge status={order.status} />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Reason: {(order as any).cancellation_reason?.trim() || "—"}
              </TooltipContent>
            </Tooltip>
          ) : (
            <OrderStatusBadge status={order.status} late={lateDep || lateArr} legs={order.legs} />
          )}
        </TableCell>
        <TableCell className="text-sm">
          {order.trip_type || ""}
        </TableCell>
        <TableCell className="text-sm whitespace-nowrap">
          <div className="font-medium">{fromLabel}</div>
          <ReadyTimeDisplay order={order} variant="compact" />
        </TableCell>
        <TableCell className="text-sm whitespace-nowrap">
          <div className="font-medium">{toLabel}</div>
          <DeliveryTimeDisplay order={order} variant="compact" />
        </TableCell>
        <TableCell className="text-sm whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          {isMultiLegParent ? (
            <span className="text-sm">
              {legTractorText || <span className="text-muted-foreground">—</span>}
            </span>
          ) : (
            <InlineAssignCell
              orderId={order.id}
              kind="tractor"
              currentId={order.assigned_tractor_id ?? null}
              currentName={order.assigned_tractor?.name ?? null}
              readOnly={isTerminal}
            />
          )}
        </TableCell>

        <TableCell onClick={(e) => e.stopPropagation()}>
          {isMultiLegParent ? (
            <span className="text-sm font-mono">
              {legTrailerIdText || <span className="text-muted-foreground">—</span>}
            </span>
          ) : (
          <InlineEditCell
            orderId={order.id}
            fieldName="trailer_id"
            currentValue={order.trailer_id}
            placeholder="e.g., TRL-001"
            orderTrailerTypeId={order.trailer_type_id}
            orderShipFromCity={order.ship_from_city}
            readOnly={isTerminal}
          />
          )}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          {isMultiLegParent ? (
            <span className="text-sm capitalize">
              {legTrailerTypeText || <span className="text-muted-foreground">—</span>}
            </span>
          ) : (
          <InlineTrailerTypeCell
            orderId={order.id}
            currentValue={order.trailer_type_id}
            fallbackValue={order.trailer_id ? loadStatuses?.[order.trailer_id]?.trailer_type_id ?? null : null}
            readOnly={isTerminal}
          />
          )}
        </TableCell>
        <TableCell className="text-sm">
          {isMultiLegParent ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <OrderLoadStatusCell
              status={order.status}
              liveLoadStatus={
                order.trailer_id ? loadStatuses?.[order.trailer_id]?.load_status ?? null : null
              }
              historical={loadStatusAtDelivery?.get(order.id) ?? null}
            />
          )}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <InlineEditCell
            orderId={order.id}
            fieldName="tender_id"
            currentValue={order.tender_id}
            placeholder="e.g., TND-001"
          />
        </TableCell>
        <TableCell className="text-sm whitespace-nowrap">
          {order.billing_party?.organization_name}
        </TableCell>
        <TableCell className="text-sm whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          {isMultiLegParent ? (
            <span className="text-sm">
              {legOperatorText || <span className="text-muted-foreground">—</span>}
            </span>
          ) : (
            <InlineAssignCell
              orderId={order.id}
              kind="operator"
              currentId={order.assigned_operator_id ?? null}
              currentName={order.assigned_operator?.name ?? null}
              readOnly={isTerminal}
            />
          )}
        </TableCell>

        <TableCell>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <BolViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
            <TenderViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
            {!isReadOnly && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => onEdit(order)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
            )}
          </div>
        </TableCell>
      </TableRow>

      {expanded && childrenLoading && (
        <TableRow className="bg-muted">
          <TableCell colSpan={16} className="py-3 bg-muted">
            <div className="flex items-center gap-2 ml-8 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading legs...
            </div>
          </TableCell>
        </TableRow>
      )}

      {expanded && childLegs?.map((child) => (
          <OrderRow
            key={child.id}
            order={child}
            onEdit={onEdit}
            onDelete={onDelete}
            isDeleting={isDeleting}
            isChild
            legNumber={child.leg_sequence || 1}
            totalLegs={childLegs.length}
            parentUniqueId={order.unique_id}
            // The sheet always shows the whole order, so a leg click selects
            // the parent — its id is in `orders`, so ?preview= resolves.
            onRowClick={() => onRowClick?.(order)}
            isSelected={selectedOrderId === order.id}
            locations={locations}
            loadStatus={child.trailer_id ? loadStatuses?.[child.trailer_id]?.load_status ?? null : null}
            legWithArrivedTrailer={legWithArrivedTrailerBySequence.get(child.leg_sequence || 1) ?? null}
            loadStatusHistorical={
              loadStatusAtDelivery?.get(child.id) ??
              // Legs aren't rows in `orders`; reuse the parent's snapshot only
              // when the leg runs the same trailer the parent mirrors.
              (child.trailer_id && child.trailer_id === order.trailer_id
                ? loadStatusAtDelivery?.get(order.id) ?? null
                : null)
            }
            childTrailerTypeLabel={
              child.trailer_type_id
                ? trailerTypesById?.get(child.trailer_type_id) ?? null
                : null
            }
          />
      ))}

      {expanded && !childrenLoading && childLegs?.length === 0 && (
        <TableRow className="bg-muted">
          <TableCell colSpan={16} className="py-2 bg-muted">
            <span className="ml-8 text-sm text-muted-foreground">No child legs found</span>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const ORDER_ACCESSORS = {
  unique_id: (o: OrderWithRelations) => o.unique_id,
  status: (o: OrderWithRelations) => o.status,
  route: (o: OrderWithRelations) => `${o.ship_from_city} ${o.ship_to_city}`,
  ready_time: (o: OrderWithRelations) => new Date(o.ready_time),
  delivery_time: (o: OrderWithRelations) => new Date(o.requested_delivery_time),
  billing_party: (o: OrderWithRelations) => o.billing_party?.organization_name,
  tractor: (o: OrderWithRelations) => o.assigned_tractor?.name ?? "",
  operator: (o: OrderWithRelations) => o.assigned_operator?.name ?? "",
};

export function OrderListView({
  orders,
  isLoading,
  isFetching = false,
  isError = false,
  error = null,
  onRetry,
  totalCount,
  totalPages,
  currentPage,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onEdit,
  onDelete,
  isDeleting,
  locations,
  hasActiveFilter = false,
}: OrderListViewProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantId } = useTenant();
  const [selectedOrder, setSelectedOrder] = useState<OrderWithRelations | null>(null);
  const { sorted: sortedOrders, sort, toggle: toggleSort } = useTableSort(orders, ORDER_ACCESSORS);

  // Re-render every 60s so late-departure/late-arrival styling updates without
  // requiring a manual refresh.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Multi-leg parents: batch-fetch per-leg trailer summaries so the parent row
  // can show comma-delimited leg trailer IDs / types without waiting for the
  // user to expand.
  const multiLegParentIds = useMemo(() => {
    if (!orders) return [] as string[];
    return orders
      .filter((o) => (typeof o.leg_count === "number" ? o.leg_count > 1 : o.leg_sequence === 0))
      .map((o) => o.id);
  }, [orders]);

  const { data: legSummariesByOrderId } = useQuery({
    queryKey: ["order-leg-asset-summaries", multiLegParentIds, tenantId],
    enabled: multiLegParentIds.length > 0 && !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("legs")
        .select(
          "order_id, leg_sequence, trailer_id, trailer_type_id, assigned_tractor_id, assigned_operator_id, assigned_operator_2_id",
        )
        .in("order_id", multiLegParentIds)
        .order("leg_sequence", { ascending: true });

      // Resolve tractor / driver display names in one batched pass — `legs`
      // has no PostgREST FK embeds to `tractors`/`drivers`.
      const tractorIds = [
        ...new Set((data ?? []).map((r: any) => r.assigned_tractor_id).filter(Boolean)),
      ] as string[];
      const driverIds = [
        ...new Set(
          (data ?? [])
            .flatMap((r: any) => [r.assigned_operator_id, r.assigned_operator_2_id])
            .filter(Boolean),
        ),
      ] as string[];
      const [tractorsRes, driversRes] = await Promise.all([
        tractorIds.length
          ? supabase.from("tractors").select("id, name").in("id", tractorIds)
          : Promise.resolve({ data: [] as any[] }),
        driverIds.length
          ? supabase.from("drivers").select("id, name").in("id", driverIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const tractorNameById = new Map<string, string>(
        ((tractorsRes as any).data ?? []).map((t: any) => [t.id as string, t.name as string]),
      );
      const driverNameById = new Map<string, string>(
        ((driversRes as any).data ?? []).map((d: any) => [d.id as string, d.name as string]),
      );


      const map = new Map<string, LegAssetSummary[]>();
      (data ?? []).forEach((row: any) => {
        const list = map.get(row.order_id) ?? [];
        list.push({
          leg_sequence: row.leg_sequence,
          trailer_id: row.trailer_id,
          trailer_type_id: row.trailer_type_id,
          tractor_name: row.assigned_tractor_id
            ? tractorNameById.get(row.assigned_tractor_id) ?? null
            : null,
          operator_names: [row.assigned_operator_id, row.assigned_operator_2_id]
            .filter(Boolean)
            .map((id: string) => driverNameById.get(id) ?? null)
            .filter(Boolean) as string[],
        });
        map.set(row.order_id, list);
      });
      return map;
    },
  });


  const { data: trailerTypes } = useTrailerTypes();
  const trailerTypesById = useMemo(() => {
    const map = new Map<string, string>();
    (trailerTypes ?? []).forEach((t) => {
      map.set(t.id, getTrailerTypeLabel(t.id, trailerTypes));
    });
    return map;
  }, [trailerTypes]);

  // Batch-fetch trailer load statuses for visible orders. Includes both the
  // parent-mirrored `trailer_id` and (for multi-leg parents) every leg's
  // `trailer_id` so expanded child rows can show their load status too.
  const trailerIds = useMemo(() => {
    if (!orders) return [] as string[];
    const ids = new Set<string>();
    orders.forEach((o) => o.trailer_id && ids.add(o.trailer_id));
    legSummariesByOrderId?.forEach((legs) =>
      legs.forEach((l) => l.trailer_id && ids.add(l.trailer_id)),
    );
    // Every leg's own trailer (order-leg-summaries), for the arrived annotation.
    orders.forEach((o) => o.legs?.forEach((l) => l.trailer_id && ids.add(l.trailer_id)));
    return [...ids];
  }, [orders, legSummariesByOrderId]);

  const { data: trailerLoadStatuses } = useQuery({
    queryKey: ["trailer-load-statuses", trailerIds, tenantId],
    enabled: trailerIds.length > 0 && !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("trailer_id, load_status, trailer_type_id, current_location_status")
        .in("trailer_id", trailerIds)
        .eq("tenant_id", tenantId!);
      const map: Record<string, { load_status: string | null; trailer_type_id: string | null; location_status?: string | null }> = {};
      data?.forEach(t => {
        map[t.trailer_id] = {
          load_status: t.load_status,
          trailer_type_id: t.trailer_type_id,
          location_status: t.current_location_status,
        };
      });
      return map;
    },
  });

  // Delivered orders show the trailer's load status *as it was at delivery* —
  // `trailers.load_status` has usually moved on to a later trip by then.
  const deliveredOrderIds = useMemo(
    () => (orders ?? []).filter((o) => o.status === "delivered").map((o) => o.id),
    [orders],
  );
  const loadStatusAtDelivery = useOrderLoadStatusAtDelivery(deliveredOrderIds);

  // Sync selected order from URL param on mount / when orders change
  useEffect(() => {
    const previewId = searchParams.get("preview");
    if (previewId && orders) {
      const found = orders.find((o) => o.id === previewId);
      setSelectedOrder(found ?? null);
    } else if (!previewId) {
      setSelectedOrder(null);
    }
  }, [searchParams, orders]);

  const handleSelectOrder = useCallback((order: OrderWithRelations | null) => {
    setSelectedOrder(order);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (order) {
        next.set("preview", order.id);
      } else {
        next.delete("preview");
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const queryClient = useQueryClient();
  const [showBulkCancel, setShowBulkCancel] = useState(false);
  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count } = useBulkSelection(orders);
  const { bulkCancelAsync, isCanceling: bulkCanceling } = useOrderCancellation();

  // Sync-then-Persist: each selected order's upstream EAPI trip is cancelled
  // before its local row is updated. Mixed success/failure is reported by the
  // hook via toast; the dialog closes regardless so the user can retry the
  // failed subset from a clean state.
  const handleBulkCancelConfirm = async (
    payload: import("./CancelOrderDialog").CancelConfirmPayload,
  ) => {
    const ids = Array.from(selectedIds);
    try {
      await bulkCancelAsync(ids, {
        reason: payload.reasonLabel,
        notes: payload.otherText || null,
        changeEventReason: payload.reason,
        changeEventOther: payload.otherText,
      });
    } finally {
      clear();
      setShowBulkCancel(false);
    }
  };

  // A failed query must never look like "no orders" — render an explicit,
  // retryable error instead of the empty state.
  const errorState = (
    <div className="rounded-lg border border-destructive/40 bg-destructive/5 py-8 px-4 text-center">
      <p className="text-sm font-medium text-destructive">Couldn't load orders</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {error?.message || "The request failed or timed out. Your connection may be slow."}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-3" onClick={() => onRetry()}>
          Retry
        </Button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {isFetching && !isLoading && !isError && (
        <div className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
        </div>
      )}
      {isError && orders !== undefined && (
        <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs">
          <span className="text-destructive">Refresh failed. Showing the last successfully loaded orders.</span>
          {onRetry && <Button variant="outline" size="sm" onClick={() => onRetry()}>Retry</Button>}
        </div>
      )}
      <BulkActionBar count={count} onClear={clear} entityLabel="order">
        <Button
          variant="ghost"
          size="sm"
          className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
          onClick={() => setShowBulkCancel(true)}
        >
          <Ban className="h-4 w-4 mr-1" />
          Cancel Orders
        </Button>
      </BulkActionBar>
      <ResponsiveTableShell
        className="flex-1 min-h-0 flex flex-col"
        cards={
          isLoading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : isError && orders === undefined ? (
            errorState
          ) : sortedOrders?.length ? (
            <div className="grid gap-3">
              {sortedOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  selected={selectedOrder?.id === order.id}
                  isChecked={selectedIds.has(order.id)}
                  onToggle={toggle}
                  onClick={handleSelectOrder}
                  onEdit={onEdit}
                  loadStatuses={trailerLoadStatuses}
                  loadStatusAtDelivery={loadStatusAtDelivery}
                  locations={locations}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border bg-card py-8 text-center text-sm text-muted-foreground">
              {hasActiveFilter ? "No orders match your filter criteria" : "No orders found"}
            </div>
          )
        }
        table={
          <div className={cn(
            "rounded-lg border bg-background flex flex-col h-full min-h-0",
            // Table renders its own overflow-auto wrapper; make it the real
            // scroll container so `position: sticky` on <thead> anchors to it.
            "[&>div]:flex-1 [&>div]:min-h-0 [&>div]:h-full",
            count > 0 && "rounded-t-none border-t-0",
          )}>
            <Table stickyColumns={3}>
          <TableHeader className="sticky top-0 z-30 [&_th]:bg-background [&_th]:border-b">
            <TableRow className="!bg-background hover:!bg-background data-[state=selected]:!bg-background !cursor-default !shadow-none [&>th]:!bg-background hover:[&>th]:!bg-background">
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                  {...(someSelected ? { "data-state": "indeterminate" } : {})}
                />
              </TableHead>
              <TableHead className="w-[40px]" />
              <TableHead><SortableHeader label="Order ID" sortKey="unique_id" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
              <TableHead><SortableHeader label="Status" sortKey="status" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
              <TableHead>Trip Type</TableHead>
              <TableHead><SortableHeader label="Ship From" sortKey="ready_time" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
              <TableHead><SortableHeader label="Ship To" sortKey="delivery_time" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
              <TableHead><SortableHeader label="Tractor" sortKey="tractor" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
              <TableHead>Trailer ID</TableHead>
              <TableHead>Trailer Type</TableHead>
              <TableHead>Load Status</TableHead>
              <TableHead>Tender ID</TableHead>
              <TableHead><SortableHeader label="Billing Party" sortKey="billing_party" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
              <TableHead><SortableHeader label="Operator" sortKey="operator" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={16} className="text-center py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : isError && orders === undefined ? (
              <TableRow>
                <TableCell colSpan={16} className="py-8">
                  {errorState}
                </TableCell>
              </TableRow>
            ) : sortedOrders?.length ? (
              sortedOrders.map((order) => (
                <ExpandableParentRow
                  key={order.id}
                  order={order}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                  onRowClick={handleSelectOrder}
                  selectedOrderId={selectedOrder?.id}
                  isChecked={selectedIds.has(order.id)}
                  onToggle={toggle}
                  loadStatuses={trailerLoadStatuses}
                  loadStatusAtDelivery={loadStatusAtDelivery}
                  locations={locations}
                  legSummary={legSummariesByOrderId?.get(order.id)}
                  trailerTypesById={trailerTypesById}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={16} className="text-center py-8 text-muted-foreground">
                  {hasActiveFilter ? "No orders match your filter criteria" : "No orders found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
          </div>
        }
      />

      <TablePagination
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={ORDERS_PAGE_SIZE_OPTIONS}
      />




      <CancelOrderDialog
        open={showBulkCancel}
        onOpenChange={setShowBulkCancel}
        orderId={`${count} order(s)`}
        onConfirm={handleBulkCancelConfirm}
        isPending={bulkCanceling}
      />

      <OrderDetailSheet
        order={selectedOrder}
        open={!!selectedOrder}
        onOpenChange={(open) => { if (!open) handleSelectOrder(null); }}
        onEdit={onEdit}
        onView={(order) => {
          const targetId = order.parent_order_id || order.id;
          const legParam = order.parent_order_id ? `?leg=${order.id}` : "";
          navigate(`/orders/${targetId}${legParam}`);
        }}
      />
    </div>
  );
}
